/**
 * Загрузчик микрофронтендов TLH.ru
 *
 * Основан на паттерне SuperAPP:
 * - Динамическая загрузка MF через import()
 * - Изолированное монтирование в отдельные контейнеры
 * - Загрузка CSS стилей как inline <style>
 * - Полная изоляция между Shell и MF
 */

import { createApiClient } from './ApiClient'
import { getEventBus, platformEvents } from './EventBus'
import type {
  ApiClient,
  LoadedMF,
  MFEventBus,
  MFExports,
  MFLoaderConfig,
  MFLoaderState,
  MFManifest,
  MountContext,
  PageContext,
  UserContext,
  WidgetMountContext,
} from './types'

/**
 * Элемент списка модулей организации (для порядка)
 */
interface OrgModuleEntry {
  id: string
  order: number
  enabled: boolean
}

/**
 * Список модулей организации
 */
interface OrgModulesList {
  modules: OrgModuleEntry[]
  updatedAt: string
}

/**
 * Класс загрузчика микрофронтендов
 */
export class MFLoader {
  private state: MFLoaderState
  private config: Required<MFLoaderConfig>
  private bus: MFEventBus
  private apiClient: ApiClient
  private getToken: () => string | null
  private orgModulesOrder: Map<string, number> = new Map()

  constructor(config: MFLoaderConfig, getToken: () => string | null) {
    this.config = {
      baseUrl: config.baseUrl,
      manifestsUrl: config.manifestsUrl ?? `${config.baseUrl}/manifests`,
      // Статический список известных модулей (загружаются из manifestsUrl/{id}.json)
      // Используется как fallback если Backend API недоступен
      knownModules: config.knownModules ?? [
        'clock',
        'consultant',
        'tagcloud',
        'presentations',
        'lms',
      ],
      loadTimeout: config.loadTimeout ?? 30000,
      cacheBusting: config.cacheBusting ?? true,
      orgId: config.orgId ?? '',
      orgSlug: config.orgSlug ?? '',
      userPermissions: config.userPermissions ?? [],
      // Использовать Backend API для загрузки манифестов
      useBackendAPI: config.useBackendAPI ?? false,
      backendAPIUrl: config.backendAPIUrl ?? '/api/consultant',
    }

    this.state = {
      mfs: new Map(),
      baseUrl: config.baseUrl,
      initialized: false,
    }

    this.bus = getEventBus()
    this.getToken = getToken
    this.apiClient = createApiClient({ baseUrl: '/api' }, getToken)
  }

  /**
   * Инициализация загрузчика - загружает манифесты всех доступных MF
   */
  async init(): Promise<MFManifest[]> {
    if (this.state.initialized) {
      return this.getManifests()
    }

    console.log('[MFLoader] Initializing...')

    try {
      const manifests = await this.loadManifests()

      // Фильтруем по правам и организации
      const filteredManifests = manifests.filter((m) => this.canLoadMF(m))

      // Регистрируем манифесты
      for (const manifest of filteredManifests) {
        this.state.mfs.set(manifest.id, {
          manifest,
          exports: null,
          status: 'pending',
          mountedContainers: new Set(),
        })
      }

      this.state.initialized = true
      console.log(
        `[MFLoader] Loaded ${filteredManifests.length} manifests:`,
        filteredManifests.map((m) => m.id)
      )

      return filteredManifests
    } catch (error) {
      console.error('[MFLoader] Failed to initialize:', error)
      throw error
    }
  }

  /**
   * Загрузить манифесты из хранилища.
   * Если useBackendAPI=true, загружает через Backend API с учётом порядка организации.
   * Иначе использует статический список knownModules.
   */
  private async loadManifests(): Promise<MFManifest[]> {
    // Если включён Backend API — пробуем загрузить через него
    if (this.config.useBackendAPI) {
      try {
        const manifests = await this.loadManifestsFromBackend()
        if (manifests.length > 0) {
          return manifests
        }
        console.warn('[MFLoader] Backend API returned empty, falling back to static list')
      } catch (error) {
        console.warn('[MFLoader] Backend API failed, falling back to static list:', error)
      }
    }

    // Fallback: загружаем из статического списка
    return this.loadManifestsFromStatic()
  }

  /**
   * Загрузить манифесты через Backend API
   * Учитывает порядок модулей организации
   */
  private async loadManifestsFromBackend(): Promise<MFManifest[]> {
    console.log('[MFLoader] Loading manifests from Backend API...')

    const baseUrl = this.config.backendAPIUrl

    // 1. Загружаем все манифесты
    const manifestsRes = await fetch(`${baseUrl}/admin/modules`)
    if (!manifestsRes.ok) {
      throw new Error(`Failed to load manifests: ${manifestsRes.status}`)
    }
    const allManifests = (await manifestsRes.json()) as MFManifest[]

    // 2. Если есть orgSlug — загружаем порядок модулей организации
    if (this.config.orgSlug) {
      try {
        const orgModulesRes = await fetch(`${baseUrl}/admin/orgs/${this.config.orgSlug}/modules`)
        if (orgModulesRes.ok) {
          const orgModules = (await orgModulesRes.json()) as OrgModulesList

          if (orgModules.modules.length > 0) {
            // Сохраняем порядок для использования в getManifests()
            this.orgModulesOrder.clear()
            orgModules.modules.forEach((m) => this.orgModulesOrder.set(m.id, m.order))

            // Фильтруем по enabled и сортируем по order
            const enabledIds = new Set(orgModules.modules.filter((m) => m.enabled).map((m) => m.id))

            const filtered = allManifests.filter((m) => enabledIds.has(m.id))
            filtered.sort((a, b) => {
              const orderA = this.orgModulesOrder.get(a.id) ?? 999
              const orderB = this.orgModulesOrder.get(b.id) ?? 999
              return orderA - orderB
            })

            console.log(
              `[MFLoader] Loaded ${filtered.length} manifests for org "${this.config.orgSlug}":`,
              filtered.map((m) => m.id)
            )
            return filtered
          }
        }
      } catch (error) {
        console.debug('[MFLoader] No org modules list, using all manifests')
      }
    }

    console.log(
      `[MFLoader] Loaded ${allManifests.length} manifests from Backend:`,
      allManifests.map((m) => m.id)
    )
    return allManifests
  }

  /**
   * Загрузить манифесты из статического списка knownModules
   */
  private async loadManifestsFromStatic(): Promise<MFManifest[]> {
    const moduleIds = this.config.knownModules

    console.log(`[MFLoader] Loading manifests for modules: ${moduleIds.join(', ')}`)

    // Загружаем манифест каждого модуля параллельно
    const manifestPromises = moduleIds.map(async (mfId) => {
      const manifestUrl = this.addCacheBusting(`${this.config.manifestsUrl}/${mfId}.json`)

      try {
        const res = await fetch(manifestUrl)
        if (!res.ok) {
          console.warn(`[MFLoader] Manifest not found for ${mfId}: ${res.status}`)
          return null
        }
        const manifest = (await res.json()) as MFManifest
        console.log(`[MFLoader] Loaded manifest: ${mfId} v${manifest.version}`)
        return manifest
      } catch (error) {
        console.warn(`[MFLoader] Failed to load manifest for ${mfId}:`, error)
        return null
      }
    })

    const results = await Promise.all(manifestPromises)
    return results.filter((m): m is MFManifest => m !== null)
  }

  /**
   * Проверить, может ли текущий пользователь загрузить MF
   */
  private canLoadMF(manifest: MFManifest): boolean {
    // Проверка авторизации
    if (manifest.requiresAuth && !this.getToken()) {
      return false
    }

    // Проверка прав
    if (manifest.permissions && manifest.permissions.length > 0) {
      const hasPermission = manifest.permissions.some((p) =>
        this.config.userPermissions.includes(p)
      )
      if (!hasPermission) {
        return false
      }
    }

    return true
  }

  /**
   * Загрузить и смонтировать MF в контейнер
   */
  async mountMF(
    mfId: string,
    container: HTMLElement,
    options?: {
      locale?: string
      theme?: 'light' | 'dark'
      user?: UserContext
      basePath?: string
      settings?: Record<string, unknown>
    }
  ): Promise<void> {
    const mf = this.state.mfs.get(mfId)
    if (!mf) {
      throw new Error(`MF "${mfId}" not found. Available: ${[...this.state.mfs.keys()].join(', ')}`)
    }

    // Если MF ещё не загружен - загружаем
    if (mf.status === 'pending' || mf.status === 'error') {
      await this.loadMF(mfId)
    }

    // Ждём загрузки если в процессе
    if (mf.status === 'loading') {
      await this.waitForLoad(mfId)
    }

    if (!mf.exports) {
      throw new Error(`MF "${mfId}" exports not available`)
    }

    // Проверяем, что стили загружены (могли быть удалены при HMR)
    await this.ensureStyles(mfId)

    // Создаём контекст монтирования
    const ctx: MountContext = {
      container,
      user: options?.user ?? this.createEmptyUser(),
      bus: this.bus,
      apiClient: this.apiClient,
      basePath: options?.basePath ?? `/mf/${mfId}`,
      locale: options?.locale ?? 'ru',
      theme: options?.theme ?? 'light',
      getToken: this.getToken,
      settings: options?.settings,
    }
    console.log(
      `[MFLoader] mountMF ${mfId}, settings:`,
      JSON.stringify(options?.settings || {}, null, 2)
    )

    // Монтируем
    console.log(`[MFLoader] Mounting ${mfId}...`)
    mf.exports.mountApp(ctx)
    mf.mountedContainers.add(container)

    // Уведомляем о монтировании
    this.bus.emit('mf:mounted', { mfId, version: mf.manifest.version })
  }

  /**
   * Смонтировать виджет MF
   */
  async mountWidget(
    mfId: string,
    widgetId: string,
    container: HTMLElement,
    options?: {
      locale?: string
      theme?: 'light' | 'dark'
      user?: UserContext
      onNavigate?: (path: string) => void
      settings?: Record<string, unknown>
    }
  ): Promise<() => void> {
    const mf = this.state.mfs.get(mfId)
    if (!mf) {
      throw new Error(`MF "${mfId}" not found`)
    }

    const widget = mf.manifest.widgets?.find((w) => w.id === widgetId)
    if (!widget) {
      throw new Error(`Widget "${widgetId}" not found in MF "${mfId}"`)
    }

    // Загружаем MF если нужно
    if (mf.status === 'pending' || mf.status === 'error') {
      await this.loadMF(mfId)
    }

    if (mf.status === 'loading') {
      await this.waitForLoad(mfId)
    }

    if (!mf.exports) {
      throw new Error(`MF "${mfId}" exports not available`)
    }

    // Проверяем, что стили загружены (могли быть удалены при HMR)
    await this.ensureStyles(mfId)

    const mountFn = mf.exports[widget.mountFunction] as
      | ((ctx: WidgetMountContext) => () => void)
      | undefined

    if (typeof mountFn !== 'function') {
      throw new Error(`Mount function "${widget.mountFunction}" not found in MF "${mfId}"`)
    }

    const ctx: WidgetMountContext = {
      container,
      user: options?.user ?? this.createEmptyUser(),
      bus: this.bus,
      apiClient: this.apiClient,
      locale: options?.locale ?? 'ru',
      theme: options?.theme ?? 'light',
      getToken: this.getToken,
      position: widget.position,
      size: widget.size,
      onNavigate: options?.onNavigate,
      settings: options?.settings,
    }

    console.log(`[MFLoader] Mounting widget ${mfId}/${widgetId}...`)
    console.log(`[MFLoader] Widget settings:`, JSON.stringify(options?.settings || {}, null, 2))
    const unmount = mountFn(ctx)
    mf.mountedContainers.add(container)

    return unmount
  }

  /**
   * Размонтировать все экземпляры MF
   * Стили УДАЛЯЮТСЯ для освобождения ресурсов
   * При повторном монтировании стили будут загружены заново через ensureStyles
   */
  async unmountMF(mfId: string): Promise<void> {
    const mf = this.state.mfs.get(mfId)
    if (!mf || !mf.exports) {
      return
    }

    console.log(`[MFLoader] Unmounting ${mfId}...`)
    mf.exports.unmountAll()
    mf.mountedContainers.clear()

    // Удаляем стили для освобождения ресурсов
    // При повторном монтировании они будут загружены заново через ensureStyles
    this.removeStyles(mfId)

    this.bus.emit('mf:unmounted', { mfId })
  }

  /**
   * Размонтировать все MF
   * Стили удаляются вместе с каждым MF
   */
  async unmountAll(): Promise<void> {
    for (const mfId of this.state.mfs.keys()) {
      await this.unmountMF(mfId)
    }
  }

  /**
   * Полная выгрузка всех MF и сброс состояния загрузчика
   * Используется при полном сбросе приложения
   */
  async unloadAll(): Promise<void> {
    await this.unmountAll()
    this.state.mfs.clear()
    this.state.initialized = false
  }

  /**
   * Загрузить MF (JS + CSS)
   */
  private async loadMF(mfId: string): Promise<void> {
    const mf = this.state.mfs.get(mfId)
    if (!mf) {
      throw new Error(`MF "${mfId}" not registered`)
    }

    mf.status = 'loading'
    console.log(`[MFLoader] Loading ${mfId}...`)

    try {
      // Используем buildHash из манифеста для cache-busting
      const buildHash = (mf.manifest as MFManifest & { buildHash?: string }).buildHash

      // Загружаем JS
      const jsUrl = this.addCacheBusting(
        `${this.config.baseUrl}${mf.manifest.entryPath}`,
        buildHash
      )
      const exports = await this.loadScript(jsUrl)

      // Валидируем экспорты
      if (typeof exports.mountApp !== 'function') {
        throw new Error(`MF "${mfId}" must export mountApp function`)
      }
      if (typeof exports.unmountAll !== 'function') {
        throw new Error(`MF "${mfId}" must export unmountAll function`)
      }

      mf.exports = exports as MFExports

      // Загружаем CSS если есть
      if (mf.manifest.stylePath) {
        const cssUrl = this.addCacheBusting(
          `${this.config.baseUrl}${mf.manifest.stylePath}`,
          buildHash
        )
        await this.loadStyles(mfId, cssUrl)
      }

      mf.status = 'loaded'
      console.log(
        `[MFLoader] Loaded ${mfId} v${exports.VERSION ?? 'unknown'} (hash: ${buildHash || 'none'})`
      )
    } catch (error) {
      mf.status = 'error'
      mf.error = error instanceof Error ? error.message : 'Unknown error'
      console.error(`[MFLoader] Failed to load ${mfId}:`, error)
      throw error
    }
  }

  /**
   * Загрузить JS модуль через Blob URL
   *
   * Vite перехватывает dynamic import() и добавляет ?import, что вызывает 500 ошибку
   * для pre-bundled файлов. Вместо этого загружаем скрипт как текст,
   * создаём Blob URL и импортируем его.
   */
  private async loadScript(url: string): Promise<Record<string, unknown>> {
    try {
      // Загружаем скрипт как текст
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error(`Failed to fetch script: ${response.status} ${response.statusText}`)
      }

      const scriptText = await response.text()

      // Создаём Blob URL
      const blob = new Blob([scriptText], { type: 'application/javascript' })
      const blobUrl = URL.createObjectURL(blob)

      try {
        // Импортируем через Blob URL - Vite не перехватывает blob: URLs
        const module = await import(/* @vite-ignore */ blobUrl)
        return module
      } finally {
        // Очищаем Blob URL
        URL.revokeObjectURL(blobUrl)
      }
    } catch (error) {
      console.error(`[MFLoader] Failed to load script: ${url}`, error)
      throw error
    }
  }

  /**
   * Загрузить CSS и добавить как inline style
   */
  private async loadStyles(mfId: string, url: string): Promise<void> {
    try {
      const response = await fetch(url)
      if (!response.ok) {
        console.warn(`[MFLoader] Failed to load styles for ${mfId}: ${response.status}`)
        return
      }

      const css = await response.text()

      // Создаём элемент style
      const style = document.createElement('style')
      style.setAttribute('data-mf', mfId)
      style.textContent = css
      document.head.appendChild(style)

      const mf = this.state.mfs.get(mfId)
      if (mf) {
        mf.styleElement = style
      }

      console.log(`[MFLoader] Loaded styles for ${mfId}`)
    } catch (error) {
      console.warn(`[MFLoader] Failed to load styles for ${mfId}:`, error)
    }
  }

  /**
   * Удалить стили MF
   */
  private removeStyles(mfId: string): void {
    const mf = this.state.mfs.get(mfId)
    if (mf?.styleElement) {
      mf.styleElement.remove()
      mf.styleElement = undefined
    }
  }

  /**
   * Убедиться, что стили MF загружены (восстановить после HMR)
   */
  private async ensureStyles(mfId: string): Promise<void> {
    const mf = this.state.mfs.get(mfId)
    if (!mf) return

    // Если стили уже загружены и есть в DOM - ничего не делаем
    if (mf.styleElement && document.head.contains(mf.styleElement)) {
      return
    }

    // Если у MF есть стили - загружаем их
    if (mf.manifest.stylePath) {
      const buildHash = (mf.manifest as MFManifest & { buildHash?: string }).buildHash
      const cssUrl = this.addCacheBusting(
        `${this.config.baseUrl}${mf.manifest.stylePath}`,
        buildHash
      )
      await this.loadStyles(mfId, cssUrl)
      console.log(`[MFLoader] Restored styles for ${mfId}`)
    }
  }

  /**
   * Ждать загрузки MF
   */
  private waitForLoad(mfId: string, timeout = 30000): Promise<void> {
    return new Promise((resolve, reject) => {
      const mf = this.state.mfs.get(mfId)
      if (!mf) {
        reject(new Error(`MF "${mfId}" not found`))
        return
      }

      if (mf.status === 'loaded') {
        resolve()
        return
      }

      if (mf.status === 'error') {
        reject(new Error(mf.error ?? 'Load failed'))
        return
      }

      const startTime = Date.now()
      const checkInterval = setInterval(() => {
        if (mf.status === 'loaded') {
          clearInterval(checkInterval)
          resolve()
        } else if (mf.status === 'error') {
          clearInterval(checkInterval)
          reject(new Error(mf.error ?? 'Load failed'))
        } else if (Date.now() - startTime > timeout) {
          clearInterval(checkInterval)
          reject(new Error(`Timeout waiting for MF "${mfId}"`))
        }
      }, 100)
    })
  }

  /**
   * Добавить cache-busting параметр к URL
   * Использует hash из манифеста для стабильного кеширования
   */
  private addCacheBusting(url: string, hash?: string): string {
    if (!this.config.cacheBusting) {
      return url
    }
    // Используем hash из манифеста или fallback на timestamp
    const cacheKey = hash || Date.now().toString()
    const separator = url.includes('?') ? '&' : '?'
    return `${url}${separator}v=${cacheKey}`
  }

  /**
   * Создать пустой контекст пользователя
   */
  private createEmptyUser(): UserContext {
    return {
      id: null,
      displayName: null,
      email: null,
      avatar: null,
      roles: [],
      orgId: null,
      orgSlug: null,
      isAuthenticated: false,
    }
  }

  // ==========================================================================
  // Публичные геттеры
  // ==========================================================================

  /**
   * Получить список загруженных манифестов
   */
  getManifests(): MFManifest[] {
    return [...this.state.mfs.values()].map((m) => m.manifest)
  }

  /**
   * Получить манифест MF по ID
   */
  getManifest(mfId: string): MFManifest | undefined {
    return this.state.mfs.get(mfId)?.manifest
  }

  /**
   * Получить статус загрузки MF
   */
  getMFStatus(mfId: string): LoadedMF['status'] | undefined {
    return this.state.mfs.get(mfId)?.status
  }

  /**
   * Загрузить MF модуль и получить его экспорты
   * Используется для доступа к функциям MF (например, mountSettingsEditor)
   */
  async loadMFModule(mfId: string): Promise<MFExports | null> {
    let mf = this.state.mfs.get(mfId)
    if (!mf) {
      console.error(`[MFLoader] MF "${mfId}" not found`)
      return null
    }

    // Если уже загружен, возвращаем экспорты
    if (mf.status === 'loaded' && mf.exports) {
      return mf.exports
    }

    // Если не загружен или в процессе — загружаем и ждём
    if (mf.status === 'pending' || mf.status === 'loading') {
      await this.loadMF(mfId)
      // Обновляем ссылку на MF после загрузки
      mf = this.state.mfs.get(mfId)
    }

    return mf?.exports ?? null
  }

  /**
   * Получить EventBus
   */
  getBus(): MFEventBus {
    return this.bus
  }

  /**
   * Проверить, инициализирован ли загрузчик
   */
  isInitialized(): boolean {
    return this.state.initialized
  }

  // ==========================================================================
  // Методы для обновления контекста
  // ==========================================================================

  /**
   * Обновить тему во всех MF
   */
  updateTheme(theme: 'light' | 'dark'): void {
    platformEvents.themeChanged(this.bus, theme)
  }

  /**
   * Обновить локаль во всех MF
   */
  updateLocale(locale: string): void {
    platformEvents.localeChanged(this.bus, locale)
  }

  /**
   * Обновить пользователя во всех MF
   */
  updateUser(user: UserContext): void {
    platformEvents.userUpdated(this.bus, user)
  }

  /**
   * Обновить контекст страницы (для глобальных виджетов типа consultant)
   */
  updatePageContext(context: PageContext): void {
    platformEvents.pageContextChanged(this.bus, context)
  }
}

// =============================================================================
// Singleton instance
// =============================================================================

let loaderInstance: MFLoader | null = null

/**
 * Получить или создать экземпляр MFLoader
 */
export function getMFLoader(config?: MFLoaderConfig, getToken?: () => string | null): MFLoader {
  if (!loaderInstance) {
    if (!config || !getToken) {
      throw new Error('MFLoader not initialized. Provide config and getToken on first call.')
    }
    loaderInstance = new MFLoader(config, getToken)
  }
  return loaderInstance
}

/**
 * Сбросить MFLoader (для тестов)
 */
export function resetMFLoader(): void {
  if (loaderInstance) {
    loaderInstance.unmountAll()
    loaderInstance = null
  }
}
